/* eslint-disable @typescript-eslint/no-explicit-any */
import { type ReactNode } from 'react';
import * as React from 'react';
import {
  documentToReactComponents,
  type Options
} from '@contentful/rich-text-react-renderer';
import { BLOCKS, MARKS } from '@contentful/rich-text-types';
import { motion } from 'framer-motion';
import { rgba } from 'polished';
import styled from 'styled-components';

import { ANIMATIONS } from '../../utils';
import { Heading, Text } from '../design-system';
import { TronDivider } from '../global/TronDivider';

const Wrapper = styled(motion.div)``;

const Inner = styled.div`
  max-width: 700px;
  margin: 60px auto 80px auto;
  padding: 0 40px;
`;

const TextWrapper = styled.div`
  margin-top: 30px;
  white-space: pre-wrap;
  column-count: 1;
  column-gap: 80px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    column-count: 1;
  }
`;

const StyledH4 = styled(Heading).attrs({ renderAs: 'h4' })`
  margin: 24px 0 16px 0;
`;

const UL = styled.ul`
  font-size: ${({ theme }) => theme.fontSize.MEDIUM};
  line-height: 28px;
  margin-bottom: 20px;
  padding-left: 20px;
  break-before: avoid-column;
  break-inside: avoid-column;
`;

const LI = styled.li`
  font-size: ${({ theme }) => theme.fontSize.MEDIUM};
  line-height: 28px;
  margin-bottom: 8px;
`;

const CodeWrapper = styled.div`
  background: ${({ theme }) => rgba(theme.highlightColor, 0.1)};
  padding: 20px;
  margin-bottom: 20px;
`;

const Pre = styled.pre`
  white-space: pre-wrap;
`;

interface TextBlockProps {
  data: string;
  title: string;
}

export const TextBlock: React.ComponentType<TextBlockProps> = React.memo(
  ({ data, title }) => {
    const json = JSON.parse(data);

    const richText: Options = {
      renderMark: {
        [MARKS.CODE]: (text: ReactNode) => <code>{text}</code>
      },
      renderNode: {
        [BLOCKS.PARAGRAPH]: (node: any, children: ReactNode) => {
          // this if statement is checking if the code text is in its own paragraph block
          // because that means it's a stand alone code snippet, instead of inline code

          // edge case: check if all the other nodes in this array are also marked as code
          // if so, they need to just render as a single code block
          if (
            node.content.length === 1 &&
            node.content[0].marks.length > 0 &&
            node.content[0].marks[0].type === 'code'
          ) {
            return (
              <CodeWrapper>
                <Pre>{children}</Pre>
              </CodeWrapper>
            );
          }
          return <Text size="medium">{children}</Text>;
        },

        [BLOCKS.HEADING_4]: (node: any, children: ReactNode) => (
          <StyledH4 renderAs="h4">{children}</StyledH4>
        ),
        [BLOCKS.UL_LIST]: (node: any, children: ReactNode) => (
          <UL>{children}</UL>
        ),
        [BLOCKS.LIST_ITEM]: (node: any, children: ReactNode) => (
          <LI>{children}</LI>
        )
      }
    };

    return (
      <Wrapper {...ANIMATIONS.SimpleFadeIn}>
        <TronDivider />
        <Inner>
          <Heading renderAs="h2">A quick guide to {title}</Heading>
          <TextWrapper>{documentToReactComponents(json, richText)}</TextWrapper>
        </Inner>
        <TronDivider flipped={true} />
      </Wrapper>
    );
  }
);
